import React from 'react';
import { Layer, Source } from 'react-mapbox-gl';
import { useSelector } from 'react-redux';

import {
  isSameYear,
  eachYearOfInterval,
  addYears,
  addDays,
  format,
} from 'date-fns';
import { ClusterLayer } from './cluster-layer';
import { makePaintStyle } from './make-paint-style';
import { makeTileSource, isDataStreamLayer } from './make-tile-source';
import { getQueryString } from '../../api';

const defaultState = {};

export const layerMapState = (layer) => ({ timelines, selectedDateRange }) => {
  if (!Object.keys(timelines).find((key) => layer.key === key)) return defaultState;
  return {
    timelines,
    selectedDateRange,
  };
};

const getGeom = ({ geom, styleType }) => {
  let finalGeom = 'geom';
  if (geom) {
    finalGeom = geom;
  } else if (styleType === 'circle' || styleType === 'heatmap') {
    finalGeom = 'geom_pt';
  }
  return finalGeom;
};

export const LayerWrapper = React.memo(
  ({
    tileSourceOptions, hasNewLayers, layer, filters, forceDots,
  }) => {
    const {
      timelines = {},
      selectedDateRange,
    } = useSelector(
      layerMapState(layer),
    );

    const querystring = useSelector(getQueryString);

    const { key, dataKey, geom } = layer;

    if (layer.survey) {
      return (
        <SurveyLayer
          hasNewLayers={hasNewLayers}
          tileSourceOptions={tileSourceOptions}
          layer={layer}
          forceDots={forceDots}
          querystring={querystring}
        />
      );
    }

    if (timelines[key]) {
      return (
        <TimelineLayer
          timeline={timelines[key]}
          tileSourceOptions={tileSourceOptions}
          layer={layer}
          filters={filters}
          selectedDateRange={selectedDateRange}
          forceDots={forceDots}
          hasNewLayers={hasNewLayers}
          querystring={querystring}
        />
      );
    }

    const styleType = layer.style.type;

    if (
      styleType === 'fill' ||
      styleType === 'line' ||
      styleType === 'heatmap' ||
      styleType === 'circle'
    ) {
      const finalGeom = getGeom({ geom, styleType });

      const tileSource = makeTileSource({
        geom: finalGeom,
        dataKey,
        layer,
        filters,
        tileSourceOptions,
        forceDots,
        querystring,
      });


      const finalStyleType = forceDots ? 'circle' : styleType;

      const sourceId = `${key}-source-${finalStyleType}`;
      const paint = makePaintStyle({
        layer,
        styleType,
        forceDots,
        hasNewLayers,
      });

      return (
        <div>
          <Source id={sourceId} tileJsonSource={tileSource} />
          <Layer
            type={finalStyleType}
            id={`${key}-layer-${finalStyleType}`}
            sourceLayer="default"
            sourceId={sourceId}
            paint={paint}
          />
        </div>
      );
    }

    if (
      styleType === 'raster'
    ) {
      const tileSource = {
        type: layer.style.type,
        tileSize: 256,
        tiles: [
          layer.url,
        ],
      };
      const finalStyleType = 'raster';
      const sourceId = `${key}-source-${finalStyleType}`;

      return (
        <div>
          <Source id={sourceId} tileJsonSource={tileSource} />
          <Layer
            type={tileSource.type}
            id={`${key}-layer-${finalStyleType}`}
            sourceLayer="default"
            sourceId={sourceId}
          />
        </div>
      );
    }

    return <ClusterLayer id={dataKey} key={dataKey} layer={layer} />;
  },
);

const getIsLayerSelected = ({
 date, selectedDateRange,
}) => {
  if (isSameYear(new Date(date), new Date(selectedDateRange.start))) return true;
  return false;
};

const SurveyLayer = ({
 tileSourceOptions, layer, hasNewLayers, forceDots, querystring,
}) => {
  const { key, dataKey, geom } = layer;

  const styleType = layer.style.type;

  const finalGeom = getGeom({ geom, styleType });
  const tileSource = makeTileSource({
    geom: finalGeom,
    dataKey,
    layer,
    tileSourceOptions,
    forceDots,
    querystring,
  });

  const finalStyleType = forceDots ? 'circle' : styleType;
  const sourceId = `${key}-source-${finalStyleType}`;
  const paint = makePaintStyle({
    layer,
    styleType,
    forceDots,
    hasNewLayers,
  });

  return (
    <div>
      <Source id={sourceId} tileJsonSource={tileSource} />
      <Layer
        type={finalStyleType}
        id={`${key}-layer-${finalStyleType}`}
        sourceLayer="default"
        sourceId={sourceId}
        paint={paint}
      />
    </div>
  );
};
const TimelineLayer = React.memo(({
  timeline,
  layer,
  tileSourceOptions,
  filters,
  selectedDateRange: originalSelectedDateRange,
  forceDots,
  hasNewLayers,
  querystring,
}) => {
  const { key, dataKey, geom } = layer;

  const styleType = layer.style.type;

  const start = new Date(timeline.start);
  const end = new Date(timeline.end);

  const finalGeom = getGeom({ geom, styleType });
  const quarterDates = eachYearOfInterval({ start, end });
  const finalStyleType = forceDots ? 'circle' : styleType;

  // Default date range is the latest available
  const selectedDateRange = (originalSelectedDateRange.start && originalSelectedDateRange.end)
    ? originalSelectedDateRange
    : {
      start: end,
      end: addYears(end, 1),
    };
  const layerElements = quarterDates.map((date, i) => {
    const sourceId = `timeline-${date.toDateString()}-${key}-source-${finalStyleType}`;
    const parsedDate = date;

    let timeRange;
    let queryOverride = {};

    // if last index and parsedDate is this year, ignore date and use is_latest flag
    if (i === quarterDates.length - 1
      && isSameYear(date, new Date())
      && timeline.displayFirstQuarter
    ) {
      queryOverride = {
        is_latest: 1,
      };
    } else if (isDataStreamLayer(layer)) {
      const beforeString = `${layer.dataStreamField}:start_date[lte]`;
      const afterString = `${layer.dataStreamField}:start_date[gte]`;
      timeRange = timeline.displayFirstQuarter
      ? [
        `${beforeString}:${format(addDays(parsedDate, 1), 'y-M-d')}`,
        `${afterString}:${format(addDays(parsedDate, -1), 'y-M-d')}`,
      ]
      : [
        `${beforeString}:${format((quarterDates[i + 1] || addYears(new Date(), 1)), 'y-M-d')}`,
        `${afterString}:${format(date, 'y-M-d')}`,
      ];

      queryOverride = {
        with_data_point: timeRange,
      };
    } else {
      const beforeString = 'before';
      const afterString = 'after';

      timeRange = timeline.displayFirstQuarter
      ? [
        `${beforeString}:${addDays(parsedDate, 1).toDateString()}`,
        `${afterString}:${addDays(parsedDate, -1).toDateString()}`,
      ]
      : [
        `${beforeString}:${(quarterDates[i + 1] || addYears(new Date(), 1)).toDateString()}`,
        `${afterString}:${date.toDateString()}`,
      ];

      queryOverride = {
        [timeline.dateColumn || 'date']: timeRange,
      };
    }

    const tileSource = makeTileSource({
      geom: finalGeom,
      dataKey,
      layer,
      filters,
      tileSourceOptions,
      forceDots,
      queryOverride: {
        source: timeline.source,
        props: timeline.key,
        ...queryOverride,
      },
      querystring,
    });

    const isSelected = getIsLayerSelected({
      date,
      selectedDateRange,
    });

    const paint = makePaintStyle({
      layer,
      styleType,
      forceDots,
      hasNewLayers,
    });

    paint[`${finalStyleType}-opacity`] = isSelected ? paint[`${finalStyleType}-opacity`] : 0;

    if (!isSelected) return null;
    return (
      <div key={sourceId}>
        <Source id={sourceId} tileJsonSource={tileSource} />

        <Layer
          type={finalStyleType}
          id={`${sourceId}-layer-${finalStyleType}`}
          sourceLayer="default"
          sourceId={sourceId}
          paint={paint}
        />
      </div>
    );
  });

  return layerElements;
});
